import React, { useEffect, useState, useMemo } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import Redeem from '../components/redeem/Redeem'
import { HOME_URL } from '../constants/navigation'
import { changeRewardsState, resetRewardsState } from '../store/actions/rewards'
import { getRewardTypes } from '../store/actions/redeem'
import { receiveRewardFromBrandPoints } from '../store/actions/points'
import { REWARDS_PROFILE_DISPATCH } from '../store/reducers/rewards/rewards'
import {
  RedeemPointsSuccess,
  RedeemPointsNoBalance,
  RedeemPointsFailed
} from '../components/popups/redeem'
import {
  DEFAULT_CONTAINER_STATE,
  REDEEM_POINTS_PROCESS_STEP
} from '../constants/containerStates'
import { ERROR_TEXT } from '../constants/messages'
import RedeemConfirmWrapper from 'wrappers/RedeemConfirmWrapper'
import i18n from '../i18n'
import filterRewardTypesForExchange from 'wrappers/filterRewardTypesForExchange'
import {
  filterSameRedeemOptions,
  formattedNftsForRedemption,
  formattedNftsForSale,
  sortRedeemOptionsByValue
} from '../util/pointsExchange.helpers'
import { buyNft, getNftsListedForSale } from '../store/actions/nft'
import SomethingWentWrong from '../components/popups/common/SomethingWentWrong'

const RedeemContainer = ({
  getRewardTypes,
  changeRewardsState,
  receiveRewardFromBrandPoints,
  rewardsProfile,
  mainProfile: {
    redemptionOptions,
    exchangeMemberships,
    currentUserPoints,
    isDisabledPointsBurn
  },
  tokenProfile,
  resetRewardsState,
  accountProfile: { email, authId },
  nftsForRedemption,
  getNftsListedForSale,
  nftsForSale,
  buyNft
}) => {
  const { t } = useTranslation()

  const navigate = useNavigate()

  useEffect(() => {
    getRewardTypes()
  }, [])

  useEffect(() => {
    getNftsListedForSale()
  }, [])

  useEffect(() => {
    if (rewardsProfile.redeemState) {
      setPopupState(rewardsProfile.redeemState)
    }
  }, [rewardsProfile.redeemState])

  useEffect(() => {
    handleNoPopup()
    return () => {
      resetRewardsState()
    }
  }, [])

  const [popupState, setPopupState] = useState()
  const [redeemOption, setRedeemOption] = useState()

  const redeemOptions = useMemo(() => {
    const filteredRewardTypes = filterRewardTypesForExchange({
      redemptionOptions,
      exchangeMemberships,
      language: i18n.language
    })
    const formattedNfts = formattedNftsForRedemption({
      nftsForRedemption,
      tokenProfile
    })
    const formattedSaleNfts = formattedNftsForSale({
      nftsForSale,
      tokenProfile
    })
    const filteredSameNfts = filterSameRedeemOptions(
      formattedNfts,
      formattedSaleNfts
    )
    return sortRedeemOptionsByValue(filteredRewardTypes, filteredSameNfts)
  }, [
    redemptionOptions,
    exchangeMemberships,
    nftsForRedemption,
    nftsForSale,
    i18n.language
  ])

  const handleNoPopup = () => {
    setPopupState(DEFAULT_CONTAINER_STATE.NONE)
  }

  const handleGiftCardSelect = (option) => {
    setPopupState(REDEEM_POINTS_PROCESS_STEP.REDEEM_POINTS)
    setRedeemOption(option)
  }

  const handlePopupSubmit = () => {
    switch (popupState) {
      case REDEEM_POINTS_PROCESS_STEP.REDEEM_POINTS:
        const neededPoints = redeemOption?.value - currentUserPoints
        if (neededPoints > 0) {
          setPopupState(REDEEM_POINTS_PROCESS_STEP.NO_ENOUGH_POINTS)
        } else if (redeemOption?.isForSale) {
          buyNft(redeemOption)
        } else {
          receiveRewardFromBrandPoints(redeemOption)
        }
        break

      case REDEEM_POINTS_PROCESS_STEP.NO_ENOUGH_POINTS:
        navigate(HOME_URL)
        break

      case REDEEM_POINTS_PROCESS_STEP.POINTS_REDEEMED:
        changeRewardsState(
          REWARDS_PROFILE_DISPATCH.REDEEM_STATE,
          DEFAULT_CONTAINER_STATE.NONE
        )
        handleNoPopup()
        break

      case REDEEM_POINTS_PROCESS_STEP.EXCHANGE_FAILED:
        changeRewardsState(
          REWARDS_PROFILE_DISPATCH.REDEEM_STATE,
          DEFAULT_CONTAINER_STATE.NONE
        )
        handleNoPopup()
        break

      default:
        break
    }
  }

  const handlePopupCancel = () => {
    switch (popupState) {
      case REDEEM_POINTS_PROCESS_STEP.REDEEM_POINTS:
        handleNoPopup()
        break
      case REDEEM_POINTS_PROCESS_STEP.NO_ENOUGH_POINTS:
        handleNoPopup()
        break

      default:
        break
    }
  }

  const renderPopup = () => {
    switch (popupState) {
      case REDEEM_POINTS_PROCESS_STEP.REDEEM_POINTS:
        return (
          <RedeemConfirmWrapper
            redeemOption={redeemOption}
            onSubmit={handlePopupSubmit}
            onCancel={handlePopupCancel}
            tokenProfile={tokenProfile}
            rewardsProfile={rewardsProfile}
            t={t}
            isDisabledPointsBurn={isDisabledPointsBurn}
            authId={authId}
            currentUserPoints={currentUserPoints}
          />
        )

      case REDEEM_POINTS_PROCESS_STEP.NO_ENOUGH_POINTS:
        return (
          <RedeemPointsNoBalance
            redeemOption={redeemOption}
            onSubmit={handlePopupSubmit}
            onCancel={handlePopupCancel}
            currentUserPoints={currentUserPoints}
            tokenProfile={tokenProfile}
            t={t}
          />
        )

      case REDEEM_POINTS_PROCESS_STEP.POINTS_REDEEMED:
        return (
          <RedeemPointsSuccess
            email={email}
            onSubmit={handlePopupSubmit}
            redeemOption={redeemOption}
            t={t}
          />
        )

      case REDEEM_POINTS_PROCESS_STEP.EXCHANGE_FAILED:
        return (
          <RedeemPointsFailed
            description={t(ERROR_TEXT.RECEIVE_REWARD_FAILED, {
              tokenName: tokenProfile.name
            })}
            onSubmit={handlePopupSubmit}
            t={t}
          />
        )

      case DEFAULT_CONTAINER_STATE.SOMETHING_WENT_WRONG:
        return <SomethingWentWrong onSubmit={handleNoPopup} t={t} />

      default:
        break
    }
  }

  return (
    <>
      <Redeem
        redeemOptions={redeemOptions}
        currentUserPoints={currentUserPoints}
        tokenProfile={tokenProfile}
        onGiftCardSelect={handleGiftCardSelect}
        t={t}
      />
      {renderPopup()}
    </>
  )
}

const mapStateToProps = ({
  mainReducer,
  accountReducer,
  rewardsReducer,
  nftReducer
}) => {
  const { mainProfile, tokenProfile } = mainReducer
  const { accountProfile } = accountReducer
  const { rewardsProfile } = rewardsReducer
  return {
    rewardsProfile,
    mainProfile,
    tokenProfile,
    accountProfile,
    nftsForRedemption: nftReducer?.nftProfile?.nftsForRedemption || [],
    nftsForSale: nftReducer?.nftProfile?.nftsForSale || []
  }
}

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      getRewardTypes,
      changeRewardsState,
      receiveRewardFromBrandPoints,
      resetRewardsState,
      getNftsListedForSale,
      buyNft
    },
    dispatch
  )

export default connect(mapStateToProps, mapDispatchToProps)(RedeemContainer)
